import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TablePagination,
  Button,
} from '@mui/material';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';

type Folder = { id: string; name: string; fileCount?: number };
type FileItem = { id: string; name: string };

export interface DirectoryMoveDialogProps {
  open: boolean;
  itemIds: string[];
  onCancel: () => void;
  onConfirm: (folderId: string | null, itemIds: string[]) => void;
  fetchFolders?: (page: number, pageSize: number) => Promise<{ total: number; items: Folder[] }>;
  fetchFiles?: (folderId: string, page: number, pageSize: number) => Promise<{ total: number; items: FileItem[] }>;
}

export const DirectoryMoveDialog: React.FC<DirectoryMoveDialogProps> = ({
  open,
  itemIds,
  onCancel,
  onConfirm,
  fetchFolders,
  fetchFiles,
}) => {
  const { t } = useTranslation();
  const [currentFolder, setCurrentFolder] = useState<{ id: string; name: string } | null>(null);
  const [viewMode, setViewMode] = useState<'folders' | 'files'>('folders');
  const [folderPage, setFolderPage] = useState(0);
  const [filesPage, setFilesPage] = useState(0);
  const [rowsPerPageMove, setRowsPerPageMove] = useState(5);
  const [selectedRoot, setSelectedRoot] = useState(false);

  // Mock data fallback
  const mockFolders = useMemo<Folder[]>(() => {
    return Array.from({ length: 13 }).map((_, i) => ({
      id: `folder-${i + 1}`,
      name: `Folder ${i + 1}`,
      fileCount: Math.floor(Math.random() * 20) + 1,
    }));
  }, []);
  const mockFilesByFolder = useMemo<Record<string, FileItem[]>>(() => {
    const map: Record<string, FileItem[]> = {};
    mockFolders.forEach((f, idx) => {
      map[f.id] = Array.from({ length: (idx % 5) + 7 }).map((_, j) => ({
        id: `${f.id}-file-${j + 1}`,
        name: `File ${j + 1} in ${f.name}`,
      }));
    });
    return map;
  }, [mockFolders]);

  // Data state to support future API integration
  const [folders, setFolders] = useState<Folder[]>([]);
  const [foldersTotal, setFoldersTotal] = useState<number>(0);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [filesTotal, setFilesTotal] = useState<number>(0);

  useEffect(() => {
    if (!open) return;
    if (viewMode !== 'folders') return;
    const load = async () => {
      if (fetchFolders) {
        const res = await fetchFolders(folderPage, rowsPerPageMove);
        setFolders(res.items);
        setFoldersTotal(res.total);
      } else {
        setFolders(mockFolders.slice(folderPage * rowsPerPageMove, folderPage * rowsPerPageMove + rowsPerPageMove));
        setFoldersTotal(mockFolders.length);
      }
    };
    load();
  }, [open, viewMode, folderPage, rowsPerPageMove, fetchFolders, mockFolders]);

  useEffect(() => {
    if (!open) return;
    if (viewMode !== 'files' || !currentFolder) return;
    const load = async () => {
      if (fetchFiles) {
        const res = await fetchFiles(currentFolder.id, filesPage, rowsPerPageMove);
        setFiles(res.items);
        setFilesTotal(res.total);
      } else {
        const all = mockFilesByFolder[currentFolder.id] || [];
        setFiles(all.slice(filesPage * rowsPerPageMove, filesPage * rowsPerPageMove + rowsPerPageMove));
        setFilesTotal(all.length);
      }
    };
    load();
  }, [open, viewMode, currentFolder, filesPage, rowsPerPageMove, fetchFiles, mockFilesByFolder]);

  useEffect(() => {
    if (!open) return;
    // Reset dialog state when opening
    setViewMode('folders');
    setCurrentFolder(null);
    setSelectedRoot(false);
    setFolderPage(0);
    setFilesPage(0);
  }, [open]);

  const handleBack = () => {
    setViewMode('folders');
    setCurrentFolder(null);
    setSelectedRoot(false);
    setFilesPage(0);
  };

  const handleFolderClick = (folder: Folder) => {
    setSelectedRoot(false);
    setCurrentFolder({ id: folder.id, name: folder.name });
    setViewMode('files');
    setFilesPage(0);
  };

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPageMove(parseInt(e.target.value, 10));
    setFolderPage(0);
    setFilesPage(0);
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {currentFolder ? (
          <>
            <IconButton aria-label="back" onClick={handleBack}>
              <ArrowBackOutlinedIcon />
            </IconButton>
            {currentFolder.name}
          </>
        ) : selectedRoot ? (
          t('referenceStore.root') || '根目录'
        ) : (
          t('referenceStore.actions.move')
        )}
      </DialogTitle>
      <DialogContent>
        {viewMode === 'folders' ? (
          <>
            <List>
              <ListItem
                key="root"
                button
                selected={selectedRoot}
                onClick={() => {
                  setSelectedRoot(true);
                  setCurrentFolder(null);
                  setViewMode('folders');
                }}
                sx={{
                  bgcolor: selectedRoot ? 'action.selected' : undefined,
                  borderRadius: 1,
                }}
              >
                <ListItemIcon>
                  <FolderOutlinedIcon />
                </ListItemIcon>
                <ListItemText
                  primary={t('referenceStore.root') || '根目录'}
                  primaryTypographyProps={{ fontWeight: selectedRoot ? 600 : undefined }}
                />
              </ListItem>
              {folders.map((folder) => (
                <ListItem key={folder.id} button onClick={() => handleFolderClick(folder)} sx={{ pl: 4 }}>
                  <ListItemIcon>
                    <FolderOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText primary={folder.name} />
                </ListItem>
              ))}
            </List>
            <TablePagination
              component="div"
              count={foldersTotal}
              page={folderPage}
              onPageChange={(_, p) => setFolderPage(p)}
              rowsPerPage={rowsPerPageMove}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </>
        ) : currentFolder ? (
          <>
            <List>
              {files.map((file) => (
                <ListItem key={file.id} sx={{ pl: 4 }}>
                  <ListItemIcon>
                    <DescriptionOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText primary={file.name} />
                </ListItem>
              ))}
            </List>
            <TablePagination
              component="div"
              count={filesTotal}
              page={filesPage}
              onPageChange={(_, p) => setFilesPage(p)}
              rowsPerPage={rowsPerPageMove}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{t('common.cancel') || 'Cancel'}</Button>
        <Button
          onClick={() => onConfirm(selectedRoot ? null : currentFolder?.id ?? null, itemIds)}
          variant="contained"
          disabled={itemIds.length === 0 || (!selectedRoot && !currentFolder)}
        >
          {t('referenceStore.actions.move') || 'Move'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
